import { Button, Card, CardBody, Image, Spinner } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { productService } from '../services/productService';
import { Creator } from '../types/creators';
import { Product } from '../types/product';

interface CreatorCardProps {
  creator?: Creator;
  creatorName?: string;
  compact?: boolean;
}

const CreatorCard: React.FC<CreatorCardProps> = ({
  creator,
  creatorName,
  compact = false,
}) => {
  // Handle both Creator object and creator name string
  const displayName = creator?.creatorName || creatorName || '';
  const isCreatorObject = !!creator;
  const { currentLocale } = useLocale();
  const countryCode = currentLocale.preferredCountryCode;

  const { data: creatorProducts, isLoading } = useQuery<Product[]>({
    queryKey: ['creatorProducts', displayName, countryCode],
    queryFn: (): Promise<Product[]> =>
      productService.getProductsByCreator(displayName, countryCode),
    enabled: !!displayName,
  });

  const _featuredProduct = creatorProducts?.[0]; // Backward compatibility if needed

  // Build thumbnail list from creator's products (first image per product)
  // Keep association to product id to enable navigation
  const thumbnails: Array<{ url: string; alt: string; id: string }> =
    useMemo(() => {
      if (!creatorProducts || creatorProducts.length === 0) return [];
      const results: Array<{ url: string; alt: string; id: string }> = [];
      for (const p of creatorProducts) {
        const url = p.mainImageUrl || (p.images && p.images[0]?.url) || '';
        if (!url) continue;
        results.push({ url, alt: p.name, id: p.id });
        if (results.length >= 7) break;
      }
      return results;
    }, [creatorProducts]);

  // Hover preview state for thumbnails
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState<boolean>(false);
  const [previewPosition, setPreviewPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const hoverTimerRef = useRef<number | null>(null);
  const PREVIEW_MAX_WIDTH = 340; // matches max-w-[340px]
  const PREVIEW_MAX_HEIGHT = 256; // matches max-h-64

  const handleThumbEnter = (url: string, targetEl: HTMLElement): void => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => {
      const rect = targetEl.getBoundingClientRect();
      const desiredLeft = rect.left;
      const maxLeft = Math.max(
        8,
        Math.min(desiredLeft, window.innerWidth - PREVIEW_MAX_WIDTH - 8)
      );

      // Prefer placing above if there isn't enough space below
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top: number;
      if (
        spaceBelow < PREVIEW_MAX_HEIGHT + 16 &&
        spaceAbove >= PREVIEW_MAX_HEIGHT + 16
      ) {
        // Place above
        top = Math.max(8, rect.top - PREVIEW_MAX_HEIGHT - 8);
      } else {
        // Place below, clamped
        top = Math.min(
          rect.bottom + 8,
          window.innerHeight - PREVIEW_MAX_HEIGHT - 8
        );
      }
      setPreviewPosition({ top, left: maxLeft });
      setPreviewUrl(url);
      setIsPreviewVisible(true);
    }, 1000);
  };

  const handleThumbLeave = (): void => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;
    setIsPreviewVisible(false);
    setPreviewUrl(null);
  };

  return (
    <Card className='h-full hover:shadow-lg transition-shadow duration-300'>
      <CardBody className='p-6'>
        <div className='text-center mb-4'>
          {/* Creator Avatar */}
          {isCreatorObject && creator.logoImageUrl ? (
            <div
              className={
                compact
                  ? 'w-12 h-12 mx-auto mb-3 overflow-hidden rounded-lg'
                  : 'w-16 h-16 mx-auto mb-3 overflow-hidden rounded-lg'
              }
            >
              <Image
                src={creator.logoImageUrl}
                alt={creator.creatorName}
                className='w-full h-full object-cover'
              />
            </div>
          ) : (
            <div
              className={
                compact
                  ? 'w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3'
                  : 'w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3'
              }
            >
              <span
                className={
                  compact
                    ? 'text-white font-bold text-lg'
                    : 'text-white font-bold text-xl'
                }
              >
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          <h3 className='text-xl font-bold text-gray-900 mb-2'>
            {displayName}
          </h3>

          {/* Show short bio if available */}
          {isCreatorObject && creator.shortBio ? (
            <p className='text-gray-600 text-sm mb-4 line-clamp-2'>
              {creator.shortBio}
            </p>
          ) : (
            <p className='text-gray-600 text-sm mb-4'>Licensed Creator</p>
          )}
        </div>

        {/* Loading Indicator for products */}
        {isLoading && (
          <div className='flex justify-center py-4'>
            <Spinner size='sm' color='primary' />
          </div>
        )}

        {/* Thumbnails collection of creator products */}
        {thumbnails.length > 0 && (
          <div className='mb-4'>
            <div className='flex flex-wrap gap-2'>
              {thumbnails.map((thumb, idx) => (
                <Link
                  key={idx}
                  to={`/products/${thumb.id}`}
                  aria-label={`View ${thumb.alt}`}
                  className={
                    compact
                      ? 'h-16 max-w-[5.5rem] p-1 bg-white border border-gray-200 rounded-md flex items-center justify-center cursor-pointer'
                      : 'h-20 max-w-[6.5rem] p-1 bg-white border border-gray-200 rounded-md flex items-center justify-center cursor-pointer'
                  }
                  onMouseEnter={e =>
                    handleThumbEnter(thumb.url, e.currentTarget as HTMLElement)
                  }
                  onMouseLeave={handleThumbLeave}
                  title={thumb.alt}
                >
                  <img
                    src={thumb.url}
                    alt={thumb.alt}
                    className='h-full w-auto object-contain'
                  />
                </Link>
              ))}

              {/* Ellipsis tile linking to collection */}
              <Link
                to={`/search?creator=${encodeURIComponent(displayName)}`}
                className={
                  compact
                    ? 'h-16 w-16 bg-gray-50 border border-gray-200 rounded-md flex items-center justify-center text-xl text-gray-500 hover:bg-gray-100'
                    : 'h-20 w-20 bg-gray-50 border border-gray-200 rounded-md flex items-center justify-center text-2xl text-gray-500 hover:bg-gray-100'
                }
                aria-label={`View ${displayName} collection`}
              >
                …
              </Link>
            </div>

            {/* Delayed hover preview rendered outside of Card via portal */}
            {isPreviewVisible &&
              previewUrl &&
              createPortal(
                <div
                  className='fixed z-[1000] p-2 bg-white border border-gray-200 rounded-lg shadow-2xl pointer-events-none max-w-[340px]'
                  style={{
                    top: previewPosition.top,
                    left: previewPosition.left,
                  }}
                >
                  <img
                    src={previewUrl}
                    alt='Preview'
                    className='max-h-64 w-auto object-contain'
                  />
                </div>,
                document.body
              )}
          </div>
        )}

        {creatorProducts && creatorProducts.length > 0 && (
          <div className='text-center text-sm text-gray-600 mb-4'>
            {creatorProducts.length} miniature
            {creatorProducts.length !== 1 ? 's' : ''} available
          </div>
        )}

        <div className='mt-auto flex gap-2'>
          {/* View Collection Button */}
          <Button
            as={Link}
            to={`/search?creator=${encodeURIComponent(displayName)}`}
            color='primary'
            variant='bordered'
            size='sm'
            className='flex-1'
          >
            View Collection
          </Button>

          {/* Creator Detail Page Button - only show for Creator objects */}
          {isCreatorObject && (
            <Button
              as={Link}
              to={`/creator/${creator.id}`}
              color='primary'
              variant='solid'
              size='sm'
              className='flex-1'
            >
              View Creator
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default CreatorCard;
